// Quotex‑style Trading UI — single‑file React component (JavaScript)
// Tailwind CSS expected for styling (no external UI libs).
// Drop into any React or Next.js project and import as <QuotexLikeUI />.

import { useMemo, useState, useEffect } from "react";

/* =============================== helpers =============================== */
const fmt = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

const pairsSeed = [
  { id: 1, name: "USD/BRL", payout: 92 },
  { id: 2, name: "USD/MXN (OTC)", payout: 78, active: true },
  { id: 3, name: "USD/BDT", payout: 93 },
  { id: 4, name: "USD/INR", payout: 93 },
];

// Fake candle generator for the preview UI only
function useCandles(count = 45) {
  const [seed, setSeed] = useState(() => Math.random() * 1000);
  useEffect(() => {
    const t = setInterval(() => setSeed((s) => s + 1), 1500);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    const arr = [];
    let last = 19.898;
    for (let i = 0; i < count; i++) {
      const drift =
        Math.sin((i + seed) * 0.45) * 0.0008 + (Math.random() - 0.5) * 0.0006;
      const open = last;
      const close = open + drift;
      const high = Math.max(open, close) + Math.random() * 0.0008;
      const low = Math.min(open, close) - Math.random() * 0.0008;
      last = close;
      arr.push({ open, close, high, low });
    }
    return arr;
  }, [seed, count]);
}

/* =============================== UI atoms =============================== */
function Dot({ className = "" }) {
  return (
    <span className={`inline-block w-1.5 h-1.5 rounded-full ${className}`} />
  );
}

function IconBtn({ title, children }) {
  return (
    <button
      title={title}
      className="p-2 rounded-xl hover:bg-white/5 transition"
    >
      {children}
    </button>
  );
}

/* ============================ main component ============================ */
export default function QuotexLikeUI() {
  const [pairs, setPairs] = useState(pairsSeed);
  const [selected, setSelected] = useState(2);
  const [amount, setAmount] = useState(6);
  const [time, setTime] = useState("11:46");
  const [pending, setPending] = useState(true);
  const [trades, setTrades] = useState([
    { id: 1, pair: "USD/MXN (OTC)", stake: 6, pnl: 0, dur: "00:00:52" },
    { id: 2, pair: "USD/MXN (OTC)", stake: 5, pnl: 0, dur: "00:00:59" },
    { id: 3, pair: "USD/MXN (OTC)", stake: 5, pnl: +9, dur: "00:01:01" },
    { id: 4, pair: "USD/MXN (OTC)", stake: 5, pnl: +9, dur: "00:01:00" },
  ]);

  const candles = useCandles(56);

  return (
    <div
      className="w-full h-full min-h-[720px] text-slate-200"
      style={{ background: "#0f1623" }}
    >
      {/* Shell grid */}
      <div className="grid grid-cols-[68px,1fr,380px] grid-rows-[64px,1fr] h-screen max-h-[900px]">
        {/* Left rail */}
        <aside className="row-span-2 bg-[#0c121c] border-r border-white/5 flex flex-col items-center py-3 gap-2">
          <button className="p-2 rounded-xl hover:bg-white/5">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 6h18M3 12h18M3 18h18"
                stroke="currentColor"
                strokeWidth="1.6"
              />
            </svg>
          </button>
          <div className="text-lg font-bold tracking-wide mt-2">QX</div>
          <div className="mt-2 w-9 h-9 rounded-xl grid place-items-center bg-white/5">
            💹
          </div>
          <nav className="mt-2 flex flex-col gap-1 text-[12px] items-center">
            {[
              { label: "TRADE", emoji: "📈" },
              { label: "SUPPORT", emoji: "🛟" },
              { label: "ACCOUNT", emoji: "👤" },
              { label: "TOURN.", emoji: "🏆" },
              { label: "MARKET", emoji: "🧮" },
              { label: "MORE", emoji: "⋯" },
            ].map((x) => (
              <div
                key={x.label}
                className="flex flex-col items-center gap-1 px-2 py-2 rounded-xl hover:bg-white/5 cursor-pointer"
              >
                <div>{x.emoji}</div>
                <div className="opacity-70">{x.label}</div>
              </div>
            ))}
          </nav>
          <div className="mt-auto mb-2 text-xs opacity-60">Help</div>
        </aside>

        {/* Top bar */}
        <header className="col-start-2 col-end-4 bg-[#0f1623] border-b border-white/5 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="font-black tracking-wide text-white text-xl">
              QUOTEX
            </div>
            <Dot className="bg-white/30" />
            <div className="text-sm text-white/70">WEB TRADING PLATFORM</div>
            {/* Pair tabs */}
            <div className="ml-6 flex gap-3">
              {pairs.map((p) => (
                <div
                  key={p.id}
                  onClick={() => setSelected(p.id)}
                  className={`group relative flex items-center gap-2 rounded-2xl border px-3 py-1.5 text-xs select-none cursor-pointer ${
                    selected === p.id
                      ? "border-emerald-400/40 bg-emerald-400/10"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                    🇺🇸
                  </span>
                  <div className="font-medium">
                    {p.name.slice(0, 10)}
                    {p.name.length > 10 ? "…" : ""}
                  </div>
                  <div className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-black/30 border border-white/10">
                    {p.payout}%
                  </div>
                  <button className="ml-2 opacity-40 hover:opacity-80">
                    ✕
                  </button>
                </div>
              ))}
              <button className="ml-1 w-8 h-8 rounded-xl bg-emerald-500 grid place-items-center text-black font-bold">
                +
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <IconBtn title="Notifications">🔔</IconBtn>
            <IconBtn title="Education">🎓</IconBtn>
            <div className="text-xs mr-2 opacity-70">DEMO ACCOUNT</div>
            <div className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-sm font-semibold">
              $106,865.17
            </div>
            <button className="ml-2 px-3 py-1.5 rounded-xl bg-emerald-500 text-black font-semibold">
              Deposit
            </button>
            <button className="px-3 py-1.5 rounded-xl bg-white/10 border border-white/10">
              Withdrawal
            </button>
          </div>
        </header>

        {/* Center – chart */}
        <main className="bg-[#0f1623] relative overflow-hidden">
          {/* chart header strip */}
          <div className="px-4 py-2 flex items-center gap-3 text-sm">
            <button className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs">
              PAIR INFORMATION
            </button>
            <div className="text-xs opacity-70">1m</div>
          </div>

          {/* chart canvas */}
          <div
            className="relative mx-3 h-[560px] rounded-lg overflow-hidden"
            style={{ background: "linear-gradient(180deg,#0f1623,#0b111a)" }}
          >
            {/* grid */}
            <div className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:56px_56px]" />
            {/* mid dashed price line */}
            <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-white/30" />
            {/* right ruler gradient */}
            <div className="absolute right-0 inset-y-0 w-24 bg-gradient-to-l from-black/20 to-transparent pointer-events-none" />

            {/* moving average */}
            <svg
              className="absolute inset-0"
              preserveAspectRatio="none"
              viewBox="0 0 1000 560"
            >
              <path
                d="M0 420 C 150 390, 220 360, 320 340 S 500 320, 650 330 S 820 360, 1000 340"
                fill="none"
                stroke="rgba(111,220,120,0.9)"
                strokeWidth="3"
              />
            </svg>

            {/* candles */}
            <div className="absolute inset-0 px-10 flex items-end gap-[6px]">
              {candles.map((c, i) => {
                const h = 420; // drawing band height
                const scale = 220000; // exaggerate for visibility
                const y = (v) => 280 - (v - 19.9) * scale; // simple mapper
                const top = Math.min(y(c.high), y(c.low));
                const wickH = Math.abs(y(c.high) - y(c.low));
                const bodyTop = Math.min(y(c.open), y(c.close));
                const bodyH = Math.max(4, Math.abs(y(c.open) - y(c.close)));
                const green = c.close >= c.open;
                return (
                  <div key={i} className="relative" style={{ height: h }}>
                    {/* wick */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 w-[2px]"
                      style={{
                        top,
                        height: wickH,
                        background: "rgba(255,255,255,0.4)",
                      }}
                    />
                    {/* body */}
                    <div
                      className={`w-5 rounded-sm ${
                        green ? "bg-emerald-500/80" : "bg-red-500/80"
                      }`}
                      style={{
                        position: "absolute",
                        top: bodyTop,
                        height: bodyH,
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* trade window markers */}
            <div className="absolute top-0 bottom-0 left-[66%] w-px bg-white/30" />
            <div className="absolute top-0 bottom-0 left-[80%] w-px bg-white/30" />
            <div className="absolute top-3 left-[66%] text-[11px] text-white/70">
              Beginning of trade
            </div>
            <div className="absolute top-3 left-[80%] text-[11px] text-white/70">
              End of trade
            </div>
          </div>

          {/* footer timeline */}
          <div className="px-6 py-3 flex items-center justify-between text-xs text-white/60">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex-1 text-center">
                11:{30 + i * 2}
              </div>
            ))}
          </div>
        </main>

        {/* Right – order panel */}
        <aside className="bg-[#0f1623] border-l border-white/5 flex flex-col">
          {/* top pair box */}
          <div className="px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-5 h-5 rounded-full bg-white/20 grid place-items-center">
                🇲🇽
              </span>
              <div className="font-semibold">USD/MXN (OTC)</div>
              <div className="ml-auto text-xs opacity-70">78%</div>
            </div>
          </div>

          <div className="px-4 py-3 text-xs flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
            <div className="uppercase tracking-wide text-white/60">
              Pending Trade
            </div>
            <label className="ml-auto inline-flex items-center gap-2 cursor-pointer select-none">
              <div
                className={`w-10 h-5 rounded-full ${
                  pending ? "bg-emerald-500" : "bg-white/20"
                } transition-all`}
              >
                <div
                  className={`h-5 w-5 rounded-full bg-white transform transition ${
                    pending ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </div>
              <input
                type="checkbox"
                className="hidden"
                checked={pending}
                onChange={() => setPending((x) => !x)}
              />
            </label>
          </div>

          {/* controls */}
          <div className="px-4 space-y-3">
            <Section title="Time">
              <NumberBox
                value={time}
                onMinus={() => {}}
                onPlus={() => {}}
                sub="Switch Time"
              />
            </Section>
            <Section title="Investment">
              <NumberBox
                value={`$ ${amount}`}
                onMinus={() => setAmount((a) => Math.max(1, a - 1))}
                onPlus={() => setAmount((a) => a + 1)}
                sub="Switch"
              />
            </Section>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <button className="h-12 rounded-xl bg-emerald-500 text-black font-semibold text-lg shadow-[0_10px_30px_rgba(16,185,129,0.25)]">
                Up
              </button>
              <button className="h-12 rounded-xl bg-[#ff5a5f] text-black font-semibold text-lg shadow-[0_10px_30px_rgba(255,90,95,0.25)]">
                Down
              </button>
            </div>

            <div className="mt-1 text-xs text-white/60">
              Your payout:{" "}
              <span className="text-white font-semibold">
                {fmt(amount * 1.78)}
              </span>{" "}
              $
            </div>
          </div>

          {/* trades list */}
          <div className="mt-4 border-t border-white/5" />
          <div className="px-4 py-2 text-sm font-semibold">
            Trades{" "}
            <span className="text-white/60 text-xs">{trades.length}</span>
          </div>
          <div className="px-3 pb-4 space-y-2 overflow-auto">
            {trades.map((t) => (
              <div
                key={t.id}
                className="rounded-xl bg-white/5 border border-white/10 p-3 text-xs"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-white/20 grid place-items-center">
                    🇲🇽
                  </span>
                  <div className="font-medium">{t.pair}</div>
                  <div className="ml-auto opacity-70">{t.dur}</div>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <Badge>💰 {t.stake}$</Badge>
                  <Badge tone={t.pnl >= 0 ? "green" : "red"}>
                    {t.pnl >= 0 ? "+" : ""}
                    {t.pnl.toFixed(2)}$
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

/* =============================== sub‑components =============================== */
function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
      <div className="text-xs uppercase tracking-wide text-white/70 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function NumberBox({ value, onMinus, onPlus, sub }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onMinus}
        className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center border border-white/10"
      >
        –
      </button>
      <div className="flex-1 h-10 rounded-lg bg-black/20 border border-white/10 grid place-items-center font-semibold">
        {value}
      </div>
      <button
        onClick={onPlus}
        className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center border border-white/10"
      >
        ＋
      </button>
      <div className="ml-2 text-[11px] text-emerald-400/90 uppercase">
        {sub}
      </div>
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-white/10 border-white/10",
    green: "bg-emerald-500/15 border-emerald-300/30 text-emerald-300",
    red: "bg-red-500/15 border-red-300/30 text-red-300",
  };
  return (
    <div className={`px-2.5 py-1 rounded-lg border text-[11px] ${tones[tone]}`}>
      {children}
    </div>
  );
}
